import { useEffect, useRef, useState, type ReactNode } from "react";
import { Mail, CheckCircle2, Clock, ArrowRight, Sparkles, Send, Palette, Settings, Calendar, Layers } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import ServiceHero, { Highlight } from "./ServiceHero";
import IncludesExcludes from "./IncludesExcludes";

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setIsVisible(true); obs.unobserve(el); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, isVisible };
}

function AnimateIn({ children, className = "", delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? "translateY(0)" : "translateY(30px)",
      transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
    }}>{children}</div>
  );
}

const setupItems = [
  { icon: <Settings className="w-4 h-4" />, text: "Configuración inicial en Brevo" },
  { icon: <Palette className="w-4 h-4" />, text: "Diseño de la identidad visual de tus correos" },
  { icon: <Sparkles className="w-4 h-4" />, text: "Configuración de automatizaciones básicas (bienvenida)" },
  { icon: <Send className="w-4 h-4" />, text: "Primer lote de correos del mes" },
];

const processSteps = [
  { n: "01", title: "Contratas el servicio", desc: "Eliges Email Marketing en tu plan." },
  { n: "02", title: "Nos das acceso", desc: "A tu cuenta de Brevo y tu base de datos." },
  { n: "03", title: "Setup en 14 días", desc: "Identidad visual, plantillas y automatizaciones." },
  { n: "04", title: "Cada mes", desc: "Hasta 4 correos diseñados y programados." },
  { n: "05", title: "Revisas", desc: "2 tandas de revisión por correo, 72h para aprobar." },
];

const EmailMarketingDemo = () => {
  return (
    <div className="space-y-28 overflow-hidden">
      {/* ━━━ HERO ━━━ */}
      <ServiceHero
        eyebrow="Email Marketing"
        title={<>Email <Highlight>Marketing</Highlight></>}
        description={
          <>
            Correos diseñados para vender y fidelizar. Diseño profesional,
            redacción persuasiva y{" "}
            <strong className="text-foreground font-semibold">
              automatizaciones que trabajan por ti.
            </strong>
          </>
        }
        primaryCta={{ label: "Contratar Email Marketing", href: "/#pricing" }}
        secondaryCta={{ label: "Agendar reunión", href: "/#contact" }}
        decoration={
          <div className="flex items-center gap-4">
            <div
              className="w-16 h-16 rounded-2xl bg-card border border-border shadow-md flex items-center justify-center text-primary"
              style={{ animation: "float 6s ease-in-out infinite" }}
            >
              <Mail className="w-8 h-8" />
            </div>
            <div className="text-sm text-muted-foreground font-body">
              Brevo · Diseño HTML · Automatizaciones
            </div>
          </div>
        }
        includes={["4 correos/mes", "4 plantillas/mes", "Automatizaciones", "Brevo"]}
      />

      {/* ━━━ INCLUYE / NO INCLUYE ━━━ */}
      <IncludesExcludes
        includes={[
          "Hasta <strong>4 correos diseñados al mes</strong> (diseño y redacción)",
          "Hasta <strong>4 plantillas nuevas al mes</strong>",
          "Automatizaciones básicas (secuencia de bienvenida y similares)",
          "Programación de envíos (si tienes base de datos o captas correos activamente)",
          "Trabajamos con <strong>Brevo</strong>",
          "Sin límite de suscriptores",
          "2 tandas de revisión por correo",
        ]}
        excludes={[
          "La licencia de Brevo (la paga el cliente)",
          "Integración de captación de correos en tu web ni configuraciones técnicas de captación",
          "Gestión del envío como servicio (diseñamos tus correos, no operamos tu email marketing)",
          "Compra o generación de bases de datos",
        ]}
      />

      {/* ━━━ SETUP INICIAL ━━━ */}
      <section className="max-w-5xl mx-auto px-4">
        <AnimateIn>
          <div className="text-center mb-10">
            <Badge variant="secondary" className="mb-4">Primeros 14 días</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-3">
              Qué incluye el setup inicial
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Dejamos todo listo para que tus correos empiecen a salir desde el primer mes.
            </p>
          </div>
        </AnimateIn>

        <div className="grid sm:grid-cols-2 gap-4">
          {setupItems.map((item, i) => (
            <AnimateIn key={i} delay={i * 0.08}>
              <div className="flex items-center gap-4 rounded-2xl border border-border bg-card p-5 hover:border-primary/40 transition-colors">
                <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  {item.icon}
                </div>
                <span className="text-sm font-medium">{item.text}</span>
                <CheckCircle2 className="w-5 h-5 text-emerald-500 ml-auto shrink-0" />
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* ━━━ SECCIÓN DESTACADA ━━━ */}
      <section className="max-w-5xl mx-auto px-4">
        <AnimateIn>
          <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-primary/[0.08] via-primary/[0.04] to-transparent shadow-xl shadow-primary/10">
            <CardContent className="p-10 md:p-14">
              <div className="flex flex-col md:flex-row items-start gap-8">
                <div className="w-16 h-16 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center shrink-0 shadow-lg shadow-primary/30">
                  <Calendar className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-4">
                    Diseñamos tus correos, tú decides cuándo llegan
                  </h3>
                  <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
                    Cada mes diseñamos y redactamos hasta 4 correos listos para enviar,
                    con plantillas adaptadas a tu marca. Si tienes base de datos propia
                    o captas correos activamente, también te dejamos programados los
                    envíos y las automatizaciones básicas funcionando.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </AnimateIn>
      </section>

      {/* ━━━ CÓMO TRABAJAMOS ━━━ */}
      <section className="max-w-5xl mx-auto px-4">
        <AnimateIn>
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">Proceso</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-3">Cómo trabajamos</h2>
          </div>
        </AnimateIn>

        <div className="grid md:grid-cols-5 gap-4">
          {processSteps.map((step, i) => (
            <AnimateIn key={step.n} delay={i * 0.08}>
              <div className="rounded-2xl border border-border bg-card p-6 h-full hover:border-primary/40 transition-colors">
                <div className="font-display text-3xl text-primary mb-3">{step.n}</div>
                <h4 className="font-bold text-sm mb-2">{step.title}</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">{step.desc}</p>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* ━━━ TANDAS DE REVISIÓN ━━━ */}
      <section className="max-w-3xl mx-auto px-4">
        <AnimateIn>
          <Card className="border-primary/20 bg-gradient-to-br from-primary/[0.05] to-transparent">
            <CardContent className="p-8 md:p-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
                  <Layers className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold">Revisiones</h3>
              </div>
              <p className="text-muted-foreground mb-4">
                Cada correo incluye <strong className="text-foreground">2 tandas de revisión</strong>.
                Una tanda equivale a un único mensaje con todos los cambios.
              </p>
              <p className="text-sm text-muted-foreground flex items-start gap-2">
                <Clock size={14} className="text-primary shrink-0 mt-0.5" />
                Dispones de 72h para enviar tus cambios; pasado el plazo, se da por aprobado.
              </p>
            </CardContent>
          </Card>
        </AnimateIn>
      </section>

      {/* ━━━ CTA FINAL ━━━ */}
      <section className="max-w-4xl mx-auto px-4">
        <AnimateIn>
          <Card className="border-primary/20 overflow-hidden relative">
            <div className="absolute -inset-10 bg-primary/5 rounded-[3rem] blur-3xl pointer-events-none" />
            <CardContent className="p-10 md:p-16 text-center relative z-10">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mx-auto mb-6">
                <Sparkles className="w-8 h-8" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                ¿Listo para vender desde la bandeja de entrada?
              </h2>
              <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
                Elige tu plan y tus primeros correos estarán listos en 14 días.
              </p>
              <Link
                to="/#pricing"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-primary text-primary-foreground font-medium hover:shadow-glow transition-all"
              >
                Contratar ahora
                <ArrowRight className="w-4 h-4" />
              </Link>
            </CardContent>
          </Card>
        </AnimateIn>
      </section>
    </div>
  );
};

export default EmailMarketingDemo;
